<script lang="ts" setup>
	import useProductActivationApi from '~/services/apis/product-activation'
	import type { NeedToKnowProps } from '~/components/Shared/need-to-know.vue'
	import type { BatchUploadSteps } from '~/components/Uploads/batch.vue'
	import { calculateAge, filterObject } from '~/utils/functions'
	import type { AddToPlanProps } from '~/pages/manage-custodian/[purchase_id]/add/new.vue'

	const props = defineProps<AddToPlanProps>()

	const { addTravelCustodian } = useProductActivationApi()
	const { urlQuery } = useUtils()

	const emits = defineEmits<{
		(event: 'update:form', payload: unknown[]): void
		(event: 'update:viewStaff', payload: boolean): void
		(event: 'done', response: NeedToKnowProps): void
	}>()

	const batchUploadGuide: BatchUploadSteps = {
		'Start here': {
			desc: 'Download the CSV/ XLS file template below',
			key: 'start'
		},
		'Fill Details': {
			desc: 'Fill in the CSV/ XLS file you just downloaded with your staff information which should include <span class="text-gray-600 font-medium">First name,Last name,Email address, Phone number, and date of birth</span>.  You can use <a href="https://docs.google.com/spreadsheets" target="_blank" class="text-blue-500 font-medium">Google sheet</a> to edit the template or save it to your desktop or any folder on your PC and edit with excel software. See sample below:',
			key: 'details'
		},
		'Upload saved file ': {
			desc: 'Upload or drag & drop the saved CSV/ XLS file you just filled on your PC.',
			key: 'upload'
		}
	}
	const formToSubmit = computed(() => props.form)
	const fetchingSlots = shallowRef(false)
	const isSubmitting = shallowRef(false)
	const needToKnow = ref<NeedToKnowProps>([
		{
			description:
				"To get things rolling, we'll send an onboarding email to your staff, so they can complete the activation process.",
			icon: {
				dir: 'fill',
				name: 'people',
				pathClass: 'fill-purple-500',
				class: 'bg-purple-50'
			},
			descClass: 'text-gray-600'
		},
		{
			description:
				"It's important to note that if a staff member doesn't activate their plan, they won't get a travel certificate.",
			icon: {
				dir: 'other',
				name: 'shield',
				pathClass: 'fill-purple-500',
				class: 'bg-purple-50'
			},
			descClass: 'text-gray-600'
		},
		{
			description: 'You can help your staff to activate their plan at any time',
			icon: {
				dir: 'fill',
				name: 'question-2',
				pathClass: 'fill-warning-500',
				class: 'bg-warning-50'
			},
			descClass: 'text-warning-500 italic'
		}
	])
	const tableColumns = computed<TableColumn>(() => {
		const staffColumn: TableColumn = !props.uploadFromEmployees
			? [
					{
						key: 'first_name',
						dataIndex: 'first_name',
						title: 'First Name',
						class: 'min-w-[20%]'
					},
					{
						key: 'last_name',
						dataIndex: 'last_name',
						title: 'Last Name',
						class: 'min-w-[20%]'
					},

					{
						key: 'email',
						dataIndex: 'email',
						title: 'Email Address',
						class: 'min-w-[20%]'
					},
					{
						key: 'phone_number',
						dataIndex: 'phone',
						title: 'Phone Number',
						class: 'min-w-[20%]'
					}
				]
			: [
					{
						key: 'employee_id',
						dataIndex: 'employee_id',
						title: props.viewStaff ? 'Staff' : 'Staff List',
						className: 'min-w-[200px]'
					}
				]
		return [
			{ key: 'sn', title: 'S/N', class: 'text-center min-w-[20%]' },
			{ key: 'location', title: 'Travel Destination', class: 'min-w-[20%]' },
			{ key: 'age', title: 'Age', class: 'min-w-[20%]' },
			{ key: 'start_date', title: 'Travel Date', class: 'min-w-[20%]' },
			{ key: 'end_date', title: 'Return Date', class: 'min-w-[20%]' },
			...staffColumn,
			{
				key: 'gender',
				title: 'Gender',
				class: 'min-w-[20%]'
			},
			{ key: 'action', title: 'Action' }
		]
	})
	const { attachStaffInfo } = useStaffList()
	const noWarningNote = shallowRef(false)
	const proceedAfterSubmission = () => {
		// @ts-ignore
		emits('done', needToKnow.value)
	}
	const setFields = async () => {
		return await resolve(async () => {
			const result = await props.fetchEmptySlot()
			if (result?.items.length) {
				result.items = result.items.map((item: unknown) => {
					const staffDetails = props.uploadFromEmployees
						? {
								employee_id: {
									value: null,
									type: 'select',
									class: 'min-w-[150px]',
									searchable: true,
									options: [],
									validations: { required: true, message: 'Select a staff from the list.' }
								}
							}
						: {
								first_name: {
									value: item?.meta?.first_name ?? '',
									disabled: !!item?.meta?.first_name
								},
								last_name: {
									value: item?.meta?.last_name ?? '',
									disabled: !!item?.meta?.last_name
								},
								phone_number: {
									value: item?.meta?.phone_number ?? '',
									type: 'tel',
									required: true,
									disabled: !!item?.meta?.phone_number,
									validations: {
										min: 11
									}
								},
								email: {
									value: item?.meta?.email ?? '',
									disabled: !!item?.meta?.email
								}
							}
					return {
						sn: true,
						temp_id: item.temp_id,
						isUsed: item?.is_used || false,
						age: {
							value: calculateAge(item?.meta?.data_of_birth ?? '0'),
							disabled: true,
							class: '!min-w-[70px]'
						},
						end_date: {
							value: item?.meta?.end_date ?? '',
							disabled: true
						},
						start_date: {
							value: item?.meta?.start_date ?? '',
							disabled: true
						},

						location: {
							value: item?.meta?.location_name ?? '',
							disabled: true
						},
						...staffDetails,
						gender: {
							value: item?.meta?.gender ?? '',
							type: 'select',
							class: '!min-w-[80px]',
							disabled: !!item?.meta?.gender,
							options: [
								{
									label: 'Male',
									value: 'male'
								},
								{
									label: 'Female',
									value: 'female'
								}
							]
						},
						isActive: true,
						editable: true,
						done: false
					}
				})
			}
			return result
		}, fetchingSlots)
	}

	const submitForm = async () => {
		const payload = {
			purchase_id: String(props.purchaseId),
			details: formToSubmit.value.map((item, i) => {
				const payload = filterObject<{
					phone_number: string
					first_name: string
					last_name: string
					temp_id: string
					email: string
				}>({ ...item, row_number: i + 1 }, ['done', 'editable', 'isActive', 'isUsed', 'sn', 'loading', 'key'])
				payload.phone_number = replacePhoneNumberToNaija(payload.phone_number)
				return payload
			})
		} as AddTravelCustodianPayload

		resolve(async () => {
			const res = await addTravelCustodian(payload)
			if (res?.data) {
				useNotification().alert({
					status: 200,
					message: res?.responseText || ''
				})
				proceedAfterSubmission()
			}
		}, isSubmitting)
	}
	const { screenSizesBreak } = useMobile()
</script>

<template>
	<section>
		<forms-policy-activation-batch-upload
			v-if="urlQuery?.method === 'batch'"
			:plan-id="String(props.purchaseId)"
			preview-img="/images/png/upload-staff-template.png"
			file-name="travel-pending-staff-template"
			:upload-steps="batchUploadGuide"
			@done="proceedAfterSubmission"
		/>
		<template v-else-if="!viewStaff">
			<uploads-add-staff-block
				v-if="!screenSizesBreak.biggerThanMd"
				:form="form"
				:data="setFields"
				:skip-user-verification="Boolean(uploadFromEmployees)"
				:submitting="isSubmitting"
				@update:value="$emit('update:form', $event)"
				@done="submitForm"
			/>

			<uploads-add-staff-table
				v-else
				:records="setFields"
				:table-columns="tableColumns"
				:loading="loading || fetchingSlots"
				:submitting="isSubmitting"
				:options="{ pagination: true, filter: { page: 1 } }"
				:deletable="false"
				file-name="add-staff-template"
				:skip-user-verification="Boolean(uploadFromEmployees)"
				class="mt-10 w-full"
				@update:value="e => $emit('update:form', e)"
				@done="submitForm"
			>
				<template #note>
					<shared-highlight-note
						v-if="!noWarningNote"
						:limit-width="false"
						default-class="bg-warning-100 border-warning-500 mt-7 max-w-[500px] text-xs mx-auto text-warning-600 font-medium"
						note="Note: Fill the input field with the accurate date of the staff traveling."
					/>
				</template>

				<template #employee_id="{ column, record }">
					<forms-staff-list
						v-if="record?.['employee_id'] && uploadFromEmployees"
						v-model:value="(record.employee_id as DynamicTableRecord).value"
						placeholder="Pick from employee list"
						:purchase-id="purchaseId"
						:name="String(column ?? 'staff-list')"
						@handle-change="e => attachStaffInfo(e, record as Record<string, DynamicTableRecord>)"
					/>
				</template>
				<template #filled-employee_id="{ record }">
					{{ record?.first_name?.value ?? '' }}
					{{ record?.last_name?.value ?? '+1' }}
				</template>
			</uploads-add-staff-table>
		</template>
		<ui-data-table v-else :columns="tableColumns" :records="fetchEmptySlot" :loading="fetchingSlots" class="mt-16">
			<template #bodyCell="{ column: { key }, record }">
				<span v-if="key === 'employee_id'">
					{{ record?.meta?.first_name ?? record?.first_name ?? 'N/A' }}
					{{ record?.meta?.last_name ?? record?.last_name ?? '' }}
				</span>
				<span v-if="key === 'first_name'">{{ record?.meta?.first_name ?? record?.first_name ?? 'N/A' }}</span>
				<span v-if="key === 'last_name'">{{ record?.meta?.last_name ?? record?.last_name ?? 'N/A' }}</span>
				<span v-if="key === 'email'">{{ record?.meta?.email ?? record?.email ?? 'N/A' }}</span>
				<span v-if="key === 'phone_number'">{{
					record?.meta?.phone_number ?? record?.phone_number ?? 'N/A'
				}}</span>
				<span v-if="key === 'age'">{{ record?.meta?.age ?? 'N/A' }}</span>
				<span v-if="key === 'gender'">{{ record?.meta?.gender ?? 'N/A' }}</span>
				<span v-if="key === 'location'">{{ record?.meta?.location ?? 'N/A' }}</span>
				<span v-if="key === 'start_date'">{{ record?.meta?.start_date ?? 'N/A' }}</span>
				<span v-if="key === 'end_date'">{{ record?.meta?.end_date ?? 'N/A' }}</span>
				<span v-if="key === 'email'">{{ record?.meta?.email ?? 'N/A' }}</span>
				<span v-if="key === 'action'">
					<button type="button" class="0 default-btn-sm mx-auto bg-primary-100 text-white" disabled>
						<lazy-tools-icon
							dir="fill"
							icon="check-2"
							:height="18"
							:width="18"
							path-class="stroke-white fill-transparent"
							class="mr-2"
						/>
						<span>Added</span>
					</button>
				</span>
			</template>
		</ui-data-table>
	</section>
</template>
